import React, { useState } from 'react';
import historyTooltip from './HistoryTooltip';
import { Bar } from 'react-chartjs-2';
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { InfoButton } from '../../FormWidgets/Description';
import { irrigationPhaseColors } from '../../Utils/database_history_utils';

const duration = (milliseconds) => {
  const seconds = Math.floor(milliseconds / 1000);
  return Math.floor(seconds / 3600) + ':' + String(Math.floor(seconds / 60) % 60).padStart(2, '0') + ':' + String(seconds % 60).padStart(2, '0');
};
const phases = [0, 1, 2, 3];

// Use server totals, never integrate the capped/zoomed activity trace in the GUI.
export default function IrrigationRuntime({ totals }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [table, setTable] = useState(false);
  const [view, setView] = useState('daily');
  const daily = view !== 'phases';
  const stacked = view === 'daily';
  if (!totals) return <Typography variant="caption" color="textSecondary">{t('database_irrigation_totals_unavailable')}</Typography>;
  const rows = [{ ...totals, day: null }, ...totals.days];
  const unknown = t('database_irrigation_unknown_phase');
  const phaseLabels = [...phases.map((phase) => 'P' + phase), unknown];
  const colours = [...irrigationPhaseColors, theme.palette.text.disabled];
  const phaseTime = (row, phase) => phase === 4 ? row.unknown_phase_ms : row.phases.find((item) => item.phase === phase)?.duration_ms ?? 0;
  const data = daily ? {
    labels: totals.days.map((day) => day.day),
    datasets: [
      ...(stacked ? phaseLabels.map((label, phase) => ({ label, backgroundColor: colours[phase],
        data: totals.days.map((day) => phaseTime(day, phase) / 60000), stack: 'runtime' })) :
        [{ label: t('database_irrigation_total'), backgroundColor: theme.palette.primary.main,
          data: totals.days.map((day) => day.total_ms / 60000) }]),
      // A gap is not pump-on time. Mark it on the baseline, outside the sum.
      { type: 'line', label: t('database_irrigation_unknown_time'), data: totals.days.map((day) => day.unknown_ms ? 0 : null),
        showLine: false, pointStyle: 'crossRot', pointRadius: 5, pointHitRadius: 10,
        borderColor: theme.palette.warning.main, backgroundColor: theme.palette.warning.main, gap: true }
    ]
  } : {
    labels: phaseLabels,
    datasets: [{ label: t('database_irrigation_totals'), data: phaseLabels.map((_, phase) => phaseTime(totals, phase) / 60000),
      backgroundColor: colours }]
  };
  const options = {
    responsive: true, maintainAspectRatio: false, animation: false,
    interaction: { intersect: false, mode: 'index' },
    scales: {
      x: { stacked, grid: { display: false }, ticks: { color: theme.palette.text.secondary, maxRotation: 0, maxTicksLimit: 7,
        callback: (value) => daily ? new Date(data.labels[value] + 'T12:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : data.labels[value] } },
      y: { stacked, beginAtZero: true, afterFit: (scale) => { scale.width = 48; },
        grid: { color: theme.palette.divider }, ticks: { color: theme.palette.text.secondary, maxTicksLimit: 5 } }
    },
    plugins: {
      legend: { display: daily, labels: { color: theme.palette.text.primary, usePointStyle: true, boxWidth: 8, font: { size: 11 } } },
      tooltip: { intersect: true, callbacks: {
        title: (items) => daily ? new Date(data.labels[items[0].dataIndex] + 'T12:00:00').toLocaleDateString() : phaseLabels[items[0].dataIndex],
        label: ({ dataset, dataIndex, datasetIndex }) => {
          const row = daily ? totals.days[dataIndex] : totals;
          const time = dataset.gap ? row.unknown_ms : view === 'total' ? row.total_ms : phaseTime(row, daily ? datasetIndex : dataIndex);
          return dataset.label + ': ' + duration(time);
        },
        footer: (items) => stacked ? t('database_irrigation_total') + ': ' + duration(totals.days[items[0].dataIndex].total_ms) : ''
      } }
    }
  };
  const title = t('database_irrigation_totals');
  return <Box mt={2} role="region" aria-label={title}>
    <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
      <Box display="flex" alignItems="center"><Typography variant="subtitle2">{title}</Typography>
        <InfoButton brief={title} content={t('database_irrigation_totals_help')} />
      </Box>
      <Box display="flex">
        <Button size="small" aria-label={title + ' · ' + t('database_graph_view')} aria-pressed={!table} onClick={() => setTable(false)}>{t('database_graph_view')}</Button>
        <Button size="small" aria-label={title + ' · ' + t('database_table_view')} aria-pressed={table} onClick={() => setTable(true)}>{t('database_table_view')}</Button>
      </Box>
    </Box>
    <Typography variant="body2">{t('database_irrigation_selected_period') + ': ' + duration(totals.total_ms)}
      {totals.unknown_ms > 0 && ' · ' + t('database_irrigation_unknown_time') + ': ' + duration(totals.unknown_ms)}
    </Typography>
    {table ? <TableContainer style={{ maxHeight: 250 }}><Table size="small" stickyHeader aria-label={title}>
      <TableHead><TableRow><TableCell>{t('database_irrigation_day')}</TableCell>
        <TableCell align="right">{t('database_irrigation_total')}</TableCell>
        {phases.map((phase) => <TableCell key={phase} align="right">{'P' + phase}</TableCell>)}
        <TableCell align="right">{unknown}</TableCell><TableCell align="right">{t('database_irrigation_unknown_time')}</TableCell>
      </TableRow></TableHead>
      <TableBody>{rows.map((row) => <TableRow key={row.day ?? 'period'}>
        <TableCell>{row.day ? new Date(row.day + 'T12:00:00').toLocaleDateString() : t('database_irrigation_selected_period')}</TableCell>
        <TableCell align="right">{duration(row.total_ms)}</TableCell>
        {phases.map((phase) => <TableCell key={phase} align="right">{duration(phaseTime(row, phase))}</TableCell>)}
        <TableCell align="right">{duration(row.unknown_phase_ms)}</TableCell>
        <TableCell align="right">{duration(row.unknown_ms)}</TableCell>
      </TableRow>)}</TableBody>
    </Table></TableContainer> : <>
      <Box display="flex" alignItems="center" flexWrap="wrap">
        <Button size="small" aria-pressed={stacked} onClick={() => setView('daily')}>{t('database_runtime_daily')}</Button>
        <Button size="small" aria-pressed={view === 'total'} onClick={() => setView('total')}>{t('database_runtime_daily_total')}</Button>
        <Button size="small" aria-pressed={!daily} onClick={() => setView('phases')}>{t('database_runtime_phases')}</Button>
        <Typography variant="caption" color="textSecondary">min</Typography>
      </Box>
      <Box height={260} position="relative" minWidth={0}><Bar data={data} options={options} plugins={[historyTooltip]} role="img" aria-label={title} /></Box>
    </>}
  </Box>;
}
